import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { clearActiveOrder } from '../lib/activeOrder';
import orderTicket from '../lib/orderTicket';
import { isNativeApp } from '../utils/platform';
import { registerDeviceForOrders } from '../utils/nativePush';
import { TURKISH_COFFEE_KITCHEN } from '../lib/kitchenRoutes';
import './ConfirmationPage.css';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const KITCHEN_LABELS = {
  st_noufer: "St Noufer's Kitchen",
  st_barbara: "St Barbara's Kitchen",
  [TURKISH_COFFEE_KITCHEN]: 'Turkish Coffee',
};

const KITCHEN_THEMES = {
  st_noufer: { bg: '#e8f4fd', border: '#1e6091', text: '#1e6091', light: '#d0eafb' },
  st_barbara: { bg: '#fce4ec', border: '#b5446e', text: '#b5446e', light: '#f8c8d8' },
  [TURKISH_COFFEE_KITCHEN]: { bg: '#2c1810', border: '#1a0d08', text: '#f3d5a8', light: '#3d2418' },
};

const DESSERTS_THEME = { bg: '#f8e8ea', border: '#8b1a2b', text: '#8b1a2b', light: '#f3d0d6' };

const statusConfig = {
  awaiting_payment: { label: 'Payment Processing', color: 'var(--warning)', bg: 'var(--warning-bg)', icon: '\u23F1', message: 'Your payment is being processed. This page will update automatically.' },
  pending: { label: 'Order Received', color: 'var(--warning)', bg: 'var(--warning-bg)', icon: '\u23F1', message: 'Show this screen at the kitchen counter.' },
  preparing: { label: 'Being Prepared', color: 'var(--info)', bg: 'var(--info-bg)', icon: '\u23F1', message: 'The kitchen is preparing your order now.' },
  ready: { label: 'Ready for Pickup!', color: 'var(--success)', bg: 'var(--success-bg)', icon: '\u2713', message: 'Your order is ready! Please collect it.' },
  collected: { label: 'Collected', color: 'var(--gray-500)', bg: 'var(--gray-100)', icon: '\u2713', message: 'Order has been collected. Enjoy your meal!' },
  cancelled: { label: 'Cancelled', color: 'var(--danger)', bg: 'var(--danger-bg)', icon: '\u2717', message: 'This order has been cancelled.' },
};


/**
 * Groups orders by kitchen and merges items/totals/notes from multiple orders
 * belonging to the same kitchen into a single virtual "kitchen card".
 */
function groupOrdersByKitchen(orders) {
  const kitchenMap = {};
  for (const order of orders) {
    const key = order.venue === 'desserts' ? 'desserts' : (order.kitchen || '_none');
    if (!kitchenMap[key]) {
      kitchenMap[key] = {
        kitchen: order.kitchen,
        venue: order.venue,
        customer_name: order.customer_name,
        orderNumbers: [],
        orderIds: [],
        items: [],
        notes: [],
        total: 0,
        statuses: [],
        payment_methods: [],
        created_at: order.created_at,
      };
    }
    const group = kitchenMap[key];
    group.orderNumbers.push(order.order_number);
    group.orderIds.push(order.id);
    for (const item of order.items) {
      group.items.push(item);
    }
    if (order.notes) group.notes.push(order.notes);
    group.total += order.total;
    group.statuses.push(order.status);
    group.payment_methods.push(order.payment_method);
    if (order.venue) group.venue = order.venue;
  }
  return Object.values(kitchenMap);
}

function KitchenCard({ group, isGrouped }) {
  const kitchen = group.kitchen;
  const isDesserts = group.venue === 'desserts';
  const theme = isDesserts ? DESSERTS_THEME : (kitchen ? KITCHEN_THEMES[kitchen] : null);
  const isCafe = group.venue === 'cafe';
  const isTurkishCoffee = kitchen === TURKISH_COFFEE_KITCHEN;

  const allCollected = group.statuses.every((s) => s === 'collected' || s === 'cancelled');
  const hasAwaitingPayment = group.statuses.some((s) => s === 'awaiting_payment');
  const isDone = allCollected;

  let status;
  if (allCollected) status = statusConfig.collected;
  else if (hasAwaitingPayment) status = statusConfig.awaiting_payment;
  else status = statusConfig.pending;

  const cardStyle = theme
    ? { borderTop: `4px solid ${theme.border}`, background: theme.bg }
    : {};

  const paymentLabel = group.payment_methods.includes('card_paid')
    ? 'Paid by Card'
    : group.payment_methods.includes('card')
      ? 'Card (processing)'
      : 'Pay at Counter';

  return (
    <div
      className={`confirmation-order-card ${isDone ? 'order-done' : ''} ${isTurkishCoffee ? 'confirmation-order-card--turkish' : ''}`}
      style={isTurkishCoffee ? undefined : cardStyle}
    >
      {theme && (isDesserts || kitchen) && (
        <div
          className="confirmation-kitchen-banner"
          style={isTurkishCoffee ? undefined : { background: theme.border, color: '#fff' }}
        >
          {isDesserts ? 'Desserts' : (KITCHEN_LABELS[kitchen] || kitchen)}
        </div>
      )}

      {!isCafe && (
        <p className="confirmation-order-number">Order Number: {group.orderNumbers.join(', ')}</p>
      )}

      {isCafe && !isDone && !hasAwaitingPayment ? (
        <div className="confirmation-cafe-banner">
          Cafe drinks and food do not require confirmation to be shown at the cafe — just look for your name on the prepared orders.
        </div>
      ) : (
        <>
          <h2 className="confirmation-title" style={{ color: status.color }}>{status.label}</h2>
          <p className="confirmation-message">
            {(() => {
              let msg = status.message;
              if (isDesserts) {
                msg = msg
                  .replace('the kitchen counter', 'the desserts table')
                  .replace('The kitchen', 'The desserts table')
                  .replace('the kitchen', 'the desserts table');
              } else if (isCafe) {
                msg = msg.replace('the kitchen counter', 'the cafe counter');
              } else if (kitchen === TURKISH_COFFEE_KITCHEN) {
                msg = msg.replace('the kitchen counter', 'the Turkish coffee stand');
              } else if (kitchen) {
                msg = msg.replace('the kitchen', KITCHEN_LABELS[kitchen] || 'the kitchen');
              }
              return msg;
            })()}
          </p>
        </>
      )}

      {!isCafe && group.customer_name && group.customer_name !== 'Guest' && (
        <div className="confirmation-customer-name">
          {group.customer_name}
        </div>
      )}

      {!isCafe && (
        <div className="confirmation-details card">
          <h3>Order Details</h3>
          <div className="confirmation-items">
            {(() => {
              const merged = [];
              for (const item of group.items) {
                const opts = item.options ? (typeof item.options === 'string' ? JSON.parse(item.options) : item.options) : null;
                const optsKey = opts ? JSON.stringify(opts) : '';
                const key = `${item.item_name}::${optsKey}`;
                const existing = merged.find((m) => m.key === key);
                if (existing) {
                  existing.quantity += item.quantity;
                } else {
                  const optEntries = opts
                    ? Object.entries(opts).filter(([, v]) => v)
                    : [];
                  merged.push({
                    key,
                    item_name: item.item_name,
                    unit_price: item.unit_price,
                    quantity: item.quantity,
                    optEntries,
                  });
                }
              }
              return merged.map((m) => (
                <div key={m.key} className="confirmation-item">
                  <div className="confirmation-item-text">
                    <span className="confirmation-item-name">
                      {m.quantity}x {m.item_name}
                    </span>
                    {m.optEntries.length > 0 && (
                      <span className="confirmation-item-options">
                        {m.optEntries.map(([k, v], i) => (
                          <span key={k} className="confirmation-item-option">
                            {k}: <strong>{v}</strong>
                            {i < m.optEntries.length - 1 ? ' · ' : ''}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                  <span className="confirmation-item-price">${(m.unit_price * m.quantity).toFixed(2)}</span>
                </div>
              ));
            })()}
          </div>
          {group.notes.length > 0 && (
            <div className="confirmation-notes">
              <strong>Notes:</strong> {group.notes.join(' | ')}
            </div>
          )}
          <div className="confirmation-total">
            <span>Subtotal</span>
            <span>${group.total.toFixed(2)}</span>
          </div>
          <div className="confirmation-payment">
            Payment: {paymentLabel}
          </div>
        </div>
      )}

    </div>
  );
}

export default function ConfirmationPage() {
  const { orderNumber, groupId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const isGroup = !!groupId;
  const justPaid = searchParams.get('paid') === '1';

  const effectiveGroupId = groupId || orderNumber;

  useEffect(() => {
    if (isGroup) {
      api.getOrderGroup(groupId)
        .then((data) => setOrders(Array.isArray(data) ? data : []))
        .catch((e) => console.error('Order group fetch error:', e))
        .finally(() => setLoading(false));
    } else {
      api.getOrder(orderNumber)
        .then((data) => setOrders([data]))
        .catch((e) => console.error('Order fetch error:', e))
        .finally(() => setLoading(false));
    }
  }, [orderNumber, groupId, isGroup]);

  useEffect(() => {
    if (loading) return;
    if (orders.length === 0) {
      // Stale deep-link / past daily period — clear the orange banner keys.
      clearActiveOrder();
      return;
    }
    const allDone = orders.every((o) => o.status === 'collected' || o.status === 'cancelled');
    if (allDone) {
      clearActiveOrder();
      return;
    }
    // The phone may still be open on an unpaid attempt. The API substitutes
    // the later paid group; remember that group so the banner and "add more"
    // follow the paid tickets rather than the stuck payment.
    const liveGroup = orders.find((o) =>
      o.order_group
      && o.status !== 'awaiting_payment'
      && o.status !== 'cancelled'
    )?.order_group;
    const replaced = Boolean(isGroup && liveGroup && liveGroup !== groupId);
    const storedGroup = replaced ? liveGroup : effectiveGroupId;
    localStorage.setItem('active_order_group', storedGroup);
    // Tag the group with the owner name so a subsequent checkout on this
    // device can only attach to it when the same person is ordering. If a
    // different customer opens this URL (shared link, push notification,
    // family device) and later orders, the CheckoutPage will see a name
    // mismatch and refuse to send add_to_group rather than silently merge
    // the two strangers' orders together.
    const ownerName = orders.find((o) => o.customer_name && o.customer_name !== 'Guest')?.customer_name
      || orders[0]?.customer_name
      || 'Guest';
    localStorage.setItem('active_order_group_owner', String(ownerName).trim().toLowerCase());
    const path = replaced
      ? `/confirmation/group/${liveGroup}`
      : (isGroup ? `/confirmation/group/${groupId}` : `/confirmation/${orderNumber}`);
    localStorage.setItem('active_order_path', path);
    if (replaced) navigate(path, { replace: true });
  }, [orders, loading, effectiveGroupId, isGroup, groupId, orderNumber, navigate]);

  useEffect(() => {
    if (orders.length === 0) return;
    const allDone = orders.every((o) => o.status === 'collected' || o.status === 'cancelled');
    if (!allDone) return;
    const timer = setTimeout(() => navigate('/'), 15000);
    return () => clearTimeout(timer);
  }, [orders, navigate]);

  const pushSubscribedRef = useRef(false);
  useEffect(() => {
    if (orders.length === 0 || pushSubscribedRef.current) return;
    const allDone = orders.every((o) => o.status === 'collected' || o.status === 'cancelled');
    if (allDone) return;

    pushSubscribedRef.current = true;
    const orderIds = orders.map((o) => o.id);

    if (isNativeApp()) {
      registerDeviceForOrders(orderIds).catch(() => {});
      return;
    }

    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    (async () => {
      try {
        if (!('Notification' in window) || Notification.permission !== 'granted') return;

        const { publicKey } = await api.getVapidPublicKey();
        const registration = await navigator.serviceWorker.ready;

        let sub = await registration.pushManager.getSubscription();
        if (!sub) {
          sub = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
          });
        }

        await api.subscribePush(sub.toJSON(), orderIds);
      } catch (e) {
        // Push not supported or user denied — app still works via polling
      }
    })();
  }, [orders]);

  useEffect(() => {
    if (orders.length === 0) return;
    const allDone = orders.every((o) => o.status === 'collected' || o.status === 'cancelled');
    if (allDone) return;

    const hasAwaitingPayment = orders.some((o) => o.status === 'awaiting_payment');
    const pollMs = hasAwaitingPayment ? 3000 : 10000;

    const interval = setInterval(() => {
      if (isGroup) {
        api.getOrderGroup(groupId).then((data) => {
          if (Array.isArray(data)) setOrders(data);
        }).catch((e) => console.error('Poll group error:', e));
      } else {
        api.getOrder(orderNumber).then((data) => setOrders([data])).catch((e) => console.error('Poll order error:', e));
      }
    }, pollMs);
    return () => clearInterval(interval);
  }, [orderNumber, groupId, isGroup, orders]);

  function refreshOrders() {
    if (isGroup) {
      api.getOrderGroup(groupId).then((data) => { if (Array.isArray(data)) setOrders(data); }).catch((e) => console.error('Refresh group error:', e));
    } else {
      api.getOrder(orderNumber).then((data) => setOrders([data])).catch((e) => console.error('Refresh order error:', e));
    }
  }

  function handleAddMore() {
    const liveGroup = orders.find((o) =>
      o.order_group
      && o.status !== 'awaiting_payment'
      && o.status !== 'cancelled'
    )?.order_group;
    const replaced = Boolean(isGroup && liveGroup && liveGroup !== groupId);
    const storedGroup = replaced ? liveGroup : effectiveGroupId;
    const path = replaced
      ? `/confirmation/group/${liveGroup}`
      : (isGroup ? `/confirmation/group/${groupId}` : `/confirmation/${orderNumber}`);
    localStorage.setItem('active_order_group', storedGroup);
    localStorage.setItem('active_order_path', path);
    navigate('/');
  }

  function handleBackToMenu() {
    clearActiveOrder();
    navigate('/');
  }

  if (loading) {
    return (
      <div className="container page-center">
        <div className="spinner" />
        <p>Loading order...</p>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="container page-center">
        <p>Order not found</p>
        <p style={{ marginTop: 8, textAlign: 'center', color: 'var(--gray-500)', fontSize: '0.9rem' }}>
          This order is no longer available (it may be from a previous day).
        </p>
        <Link to="/" className="btn btn-primary" onClick={clearActiveOrder}>
          Back to Menu
        </Link>
      </div>
    );
  }

  const accepted = orderTicket.acceptedOrders(orders);
  if (accepted.length === 0) {
    return (
      <div className="container page-center">
        <p>This payment was not completed.</p>
        <p style={{ marginTop: 8, textAlign: 'center', color: 'var(--gray-500)', fontSize: '0.9rem' }}>
          This screen is not an order confirmation.
        </p>
        <button type="button" className="btn btn-primary" onClick={handleBackToMenu}>
          Back to Menu
        </button>
      </div>
    );
  }

  const liveOrders = accepted.filter((o) => o.status !== 'cancelled');
  const shownOrders = liveOrders.length > 0 ? liveOrders : accepted;
  const kitchenGroups = groupOrdersByKitchen(shownOrders);
  const isMultiKitchen = kitchenGroups.length > 1;
  const grandTotal = liveOrders.reduce((sum, o) => sum + o.total, 0);
  const firstOrder = shownOrders[0];
  const allCollected = shownOrders.every((o) => o.status === 'collected' || o.status === 'cancelled');

  return (
    <main className={`confirmation-page ${allCollected ? 'all-collected' : ''}`}>
      <div className="confirmation-card container">
        <button
          type="button"
          className="confirmation-top-back"
          onClick={() => {
            if (allCollected) {
              handleBackToMenu();
            } else {
              navigate('/');
            }
          }}
        >
          &larr; Back
        </button>

        {justPaid && !allCollected && (
          <div className="confirmation-paid-banner">
            Payment Successful
          </div>
        )}

        {!allCollected && shownOrders.some((o) => !o.pos_station_id) && (
          <div className="confirmation-pickup-notice" role="status">
            Online orders to be picked up by 11:45 am
          </div>
        )}

        {isMultiKitchen && !allCollected && (
          <div className="confirmation-group-header">
            <h1>Your Orders</h1>
            <p className="confirmation-group-hint">
              {kitchenGroups.some((g) => g.venue === 'desserts')
                ? `Your order is in ${kitchenGroups.length} places. Show the desserts confirmation at the desserts table.`
                : `Your order spans ${kitchenGroups.length} kitchens. Please show each confirmation at the correct kitchen.`}
            </p>
          </div>
        )}

        {allCollected && (
          <div className="collected-overlay">
            <div className="collected-stamp">COLLECTED</div>
          </div>
        )}

        {kitchenGroups.map((group) => (
          <KitchenCard
            key={group.kitchen || '_none'}
            group={group}
            isGrouped={isMultiKitchen}
          />
        ))}

        {isMultiKitchen && !allCollected && (
          <div className="confirmation-grand-total">
            <span>Grand Total</span>
            <span>${grandTotal.toFixed(2)}</span>
          </div>
        )}

        <button
          type="button"
          className="btn btn-outline btn-block confirmation-receipt-btn"
          onClick={() => {
            const id = isGroup ? `group/${effectiveGroupId}` : firstOrder.order_number;
            window.open(`/receipt/${id}`, '_blank');
          }}
        >
          Download Receipt
        </button>

        {allCollected ? (
          <button onClick={handleBackToMenu} className="btn btn-primary btn-block confirmation-back-btn">
            Back to Menu
          </button>
        ) : (
          <button onClick={handleAddMore} className="btn btn-outline btn-block">
            Add More Items
          </button>
        )}
      </div>
    </main>
  );
}
